package com.arenaproplay.admin.pages.system

import androidx.compose.runtime.Composable
import com.varabyte.kobweb.compose.foundation.layout.Box
import com.varabyte.kobweb.compose.foundation.layout.Column
import com.varabyte.kobweb.compose.foundation.layout.Row
import com.varabyte.kobweb.compose.ui.Alignment
import com.varabyte.kobweb.compose.ui.Modifier
import com.varabyte.kobweb.compose.ui.modifiers.*
import com.varabyte.kobweb.compose.ui.styleModifier
import com.varabyte.kobweb.silk.components.text.SpanText
import com.arenaproplay.admin.domain.admin.PageResult
import com.arenaproplay.admin.domain.admin.UserRow
import com.arenaproplay.admin.ui.components.admin.*
import com.arenaproplay.admin.ui.theme.AppColors
import com.arenaproplay.admin.ui.theme.bodyMMedium
import com.arenaproplay.admin.ui.theme.bodyMRegular
import com.arenaproplay.admin.ui.theme.bodySMedium
import com.arenaproplay.admin.ui.theme.toRgbaString
import com.arenaproplay.admin.ui.theme.toRgbString
import com.arenaproplay.admin.ui.utils.formatTimestampShort
import org.jetbrains.compose.web.css.*

// ── Constants ────────────────────────────────────────────────────────────────

const val SYSTEM_ADMINS_PAGE_SIZE = 25

// ── Private helpers ───────────────────────────────────────────────────────────

private fun sysAdminRolePillTone(role: String): PillTone {
    val r = role.uppercase().replace("_", "").replace("-", "")
    return when {
        r.contains("SUPER") -> PillTone.Pro
        r.contains("ADMIN") -> PillTone.Accent
        else -> PillTone.Neutral
    }
}

private fun sysAdminStatusPillTone(status: String): PillTone = when (status.uppercase()) {
    "ACTIVE" -> PillTone.Success
    "BANNED", "BLOCKED", "DELETED" -> PillTone.Danger
    "PENDING" -> PillTone.Warning
    else -> PillTone.Neutral
}

private fun sysAdminStatusLabel(status: String): String = when (status.uppercase()) {
    "ACTIVE" -> "Активен"
    "BANNED" -> "Заблокирован"
    "BLOCKED" -> "Заблокирован"
    "DELETED" -> "Удалён"
    "PENDING" -> "Ожидает"
    else -> status
}

// ─────────────────────────────────────────────────────────────────────────────
// TAB 1: АДМИНЫ
// ─────────────────────────────────────────────────────────────────────────────

@Composable
fun SystemAdminsTab(
    loadState: LoadState,
    data: PageResult<UserRow>?,
    admins: List<UserRow>,
    page: Int,
    onPagePrev: () -> Unit,
    onPageNext: () -> Unit,
) {
    if (loadState.error != null) {
        ErrorCard(loadState)
        return
    }

    // Info banner: role grants pending API support
    Box(
        Modifier
            .fillMaxWidth()
            .styleModifier {
                property("background", AppColors.Accent.WarningTint.toRgbString())
                property("border", "1px solid ${AppColors.Warning.toRgbaString(0.22f)}")
                property("border-radius", "12px")
                property("padding", "14px 18px")
            }
    ) {
        Row(Modifier.fillMaxWidth(), verticalAlignment = Alignment.CenterVertically) {
            Box(
                Modifier
                    .width(28.px)
                    .height(28.px)
                    .borderRadius(50.percent)
                    .styleModifier {
                        property("background", AppColors.Warning.toRgbaString(0.16f))
                        property("flex-shrink", "0")
                    },
                contentAlignment = Alignment.Center,
            ) {
                SpanText(
                    "!",
                    Modifier.bodySMedium().color(AppColors.Warning)
                        .styleModifier { property("font-weight", "700") },
                )
            }
            HSpace(12)
            Column(Modifier.weight(1f)) {
                SpanText("Выдача ролей ожидает API", Modifier.bodyMMedium().color(AppColors.Ink))
                Box(Modifier.height(3.px))
                SpanText(
                    "Назначение и отзыв роли ADMIN пока не реализованы на уровне API. Список доступен только для просмотра.",
                    Modifier.bodySMedium().color(AppColors.Muted),
                )
            }
        }
    }

    VSpace(20)

    SectionCard(
        title = "Администраторы",
        subtitle = "Пользователи с ролью ADMIN или SUPER_ADMIN",
    ) {
        DataTable(
            items = admins,
            loading = data == null,
            columns = listOf(
                TableColumn<UserRow>(
                    header = "АДМИНИСТРАТОР",
                    weight = 2.0f,
                    sortKey = { it.name },
                ) { row ->
                    Row(verticalAlignment = Alignment.CenterVertically) {
                        Box(
                            Modifier
                                .width(34.px)
                                .height(34.px)
                                .borderRadius(50.percent)
                                .styleModifier {
                                    val idx = kotlin.math.abs(row.name.hashCode()) % AppColors.AvatarPalette.size
                                    property("background", AppColors.AvatarPalette[idx])
                                    property("flex-shrink", "0")
                                },
                            contentAlignment = Alignment.Center,
                        ) {
                            SpanText(
                                row.name.firstOrNull()?.uppercase() ?: "A",
                                Modifier.bodySMedium()
                                    .styleModifier {
                                        property("color", "#05080F")
                                        property("font-weight", "700")
                                        property("font-size", "11px")
                                        property("line-height", "1")
                                    },
                            )
                        }
                        HSpace(10)
                        Column {
                            SpanText(row.name, Modifier.bodyMMedium().color(AppColors.Ink))
                            Box(Modifier.height(2.px))
                            SpanText(
                                row.email,
                                Modifier.bodySMedium().color(AppColors.Muted)
                                    .styleModifier { property("font-size", "11px") },
                            )
                        }
                    }
                },
                TableColumn<UserRow>(
                    header = "РОЛЬ",
                    weight = 1.0f,
                ) { row ->
                    Pill(row.role, sysAdminRolePillTone(row.role))
                },
                TableColumn<UserRow>(
                    header = "ЗАРЕГИСТРИРОВАН",
                    weight = 1.1f,
                    sortKey = { it.createdAt },
                ) { row ->
                    SpanText(
                        formatTimestampShort(row.createdAt),
                        Modifier.bodyMRegular().color(AppColors.Muted)
                            .styleModifier { property("font-size", "11px") },
                    )
                },
                TableColumn<UserRow>(
                    header = "АКТИВНОСТЬ",
                    weight = 1.1f,
                    sortKey = { it.lastActive },
                ) { row ->
                    SpanText(
                        formatTimestampShort(row.lastActive),
                        Modifier.bodyMRegular().color(AppColors.Faint)
                            .styleModifier { property("font-size", "11px") },
                    )
                },
                TableColumn<UserRow>(
                    header = "СТАТУС",
                    weight = 0.9f,
                ) { row ->
                    Pill(sysAdminStatusLabel(row.status), sysAdminStatusPillTone(row.status))
                },
            ),
            emptyText = "На этой странице нет администраторов",
        )
    }

    VSpace(16)

    PaginationBar(
        page = page,
        pageSize = data?.pageSize ?: SYSTEM_ADMINS_PAGE_SIZE,
        total = data?.total ?: 0,
        onPrev = onPagePrev,
        onNext = onPageNext,
    )

    VSpace(28)
}
